import type {
  LoadingContextValue,
  LoadingStep,
  LoadingStepState,
} from "./types";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";

import { DEFAULT_LOADING_CONFIG } from "./types";
import { LoadingProgressContext } from "./LoadingProgressContext";

interface LoadingProgressProviderProps {
  children: ReactNode;
}

/**
 * Provider pour le système de progression de chargement
 *
 * Ce provider gère l'état global de la progression et expose les actions
 * aux composants enfants via le contexte.
 *
 * ⚠️ IMPORTANT: Ce provider est utilisé INTERNEMENT par PageLoader.
 *    Ne l'utilisez pas directement dans vos composants.
 */
export function LoadingProgressProvider({
  children,
}: LoadingProgressProviderProps) {
  const [steps, setSteps] = useState<LoadingStepState[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // Refs pour les timers de warning
  const stuckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentStepRef = useRef<string | null>(null);

  // Fonction de log conditionnelle
  const log = useCallback(
    (level: "log" | "warn" | "error", message: string, ...args: unknown[]) => {
      if (DEFAULT_LOADING_CONFIG.enableDebugLogs || level === "error") {
        const prefix = DEFAULT_LOADING_CONFIG.logPrefix;
        console[level](`${prefix} ${message}`, ...args);
      }
    },
    [],
  );

  // Démarre un timer d'avertissement si une étape prend trop longtemps
  const startStuckWarningTimer = useCallback((stepId: string) => {
    // Clear le timer précédent
    if (stuckTimerRef.current) {
      clearTimeout(stuckTimerRef.current);
    }

    stuckTimerRef.current = setTimeout(() => {
      console.warn(
        `%c[LoadingProgress] ⏰ ATTENTION: L'étape "${stepId}" est bloquée depuis ${DEFAULT_LOADING_CONFIG.stuckWarningTimeout / 1000}s!

📋 CHECKLIST DE DEBUG:
   □ L'opération async est-elle en cours ?
   □ Y a-t-il une erreur silencieuse non catchée ?
   □ Avez-vous oublié d'appeler completeStep('${stepId}') ?
   □ Le composant a-t-il été démonté prématurément ?

💡 CONSEIL: Ajoutez des try/catch avec failStep() pour capturer les erreurs.
`,
        "color: orange; font-weight: bold",
      );
    }, DEFAULT_LOADING_CONFIG.stuckWarningTimeout);

    currentStepRef.current = stepId;
  }, []);

  // Clear le timer quand une étape est complétée
  const clearStuckWarningTimer = useCallback(() => {
    if (stuckTimerRef.current) {
      clearTimeout(stuckTimerRef.current);
      stuckTimerRef.current = null;
    }
    currentStepRef.current = null;
  }, []);

  // Initialise les étapes
  const initSteps = useCallback(
    (newSteps: LoadingStep[]) => {
      if (newSteps.length === 0) {
        log("warn", "initSteps appelé avec un tableau vide");
        return;
      }

      const stepsWithState: LoadingStepState[] = newSteps.map(
        (step, index) => ({
          ...step,
          status: index === 0 ? "in-progress" : "pending",
        }),
      );

      setSteps(stepsWithState);
      setErrorMessage(undefined);

      // Démarre le timer pour la première étape
      startStuckWarningTimer(newSteps[0].id);

      log("log", `Initialisé avec ${newSteps.length} étapes`);
    },
    [log, startStuckWarningTimer],
  );

  // Démarre une étape
  const startStep = useCallback(
    (stepId: string) => {
      setSteps((prev) => {
        const stepIndex = prev.findIndex((s) => s.id === stepId);
        if (stepIndex === -1) {
          log("error", `Étape "${stepId}" non trouvée!`);
          return prev;
        }

        const updated = [...prev];
        updated[stepIndex] = { ...updated[stepIndex], status: "in-progress" };
        return updated;
      });

      startStuckWarningTimer(stepId);
    },
    [log, startStuckWarningTimer],
  );

  // Complète une étape
  const completeStep = useCallback(
    (stepId: string) => {
      clearStuckWarningTimer();

      setSteps((prev) => {
        const stepIndex = prev.findIndex((s) => s.id === stepId);
        if (stepIndex === -1) {
          log("error", `Étape "${stepId}" non trouvée pour complétion!`);
          return prev;
        }

        const updated = [...prev];
        updated[stepIndex] = { ...updated[stepIndex], status: "completed" };

        // Démarrer la prochaine étape pending
        const nextPendingIndex = updated.findIndex(
          (s) => s.status === "pending",
        );
        if (nextPendingIndex !== -1) {
          updated[nextPendingIndex] = {
            ...updated[nextPendingIndex],
            status: "in-progress",
          };
          startStuckWarningTimer(updated[nextPendingIndex].id);
        }

        return updated;
      });
    },
    [log, clearStuckWarningTimer, startStuckWarningTimer],
  );

  // Marque une étape en erreur
  const failStep = useCallback(
    (stepId: string, message: string) => {
      clearStuckWarningTimer();

      setSteps((prev) => {
        const stepIndex = prev.findIndex((s) => s.id === stepId);
        if (stepIndex === -1) {
          log("error", `Étape "${stepId}" non trouvée pour erreur!`);
          return prev;
        }

        const updated = [...prev];
        updated[stepIndex] = {
          ...updated[stepIndex],
          status: "error",
          error: message,
        };
        return updated;
      });

      setErrorMessage(message);
      log("error", `Erreur sur "${stepId}": ${message}`);
    },
    [log, clearStuckWarningTimer],
  );

  // Réinitialise tout
  const reset = useCallback(() => {
    clearStuckWarningTimer();
    setSteps([]);
    setErrorMessage(undefined);
    log("log", "Reset effectué");
  }, [log, clearStuckWarningTimer]);

  // Calcul des valeurs dérivées
  const computedValues = useMemo(() => {
    if (steps.length === 0) {
      return {
        progress: 0,
        currentLabel: "Chargement...",
        isComplete: false,
        hasError: false,
      };
    }

    const totalWeight = steps.reduce((sum, s) => sum + s.weight, 0);
    const completedWeight = steps
      .filter((s) => s.status === "completed")
      .reduce((sum, s) => sum + s.weight, 0);

    const progress = Math.round((completedWeight / totalWeight) * 100);

    const currentStep = steps.find((s) => s.status === "in-progress");
    const currentLabel = currentStep?.label || "Chargement...";

    const isComplete = steps.every((s) => s.status === "completed");
    const hasError = steps.some((s) => s.status === "error");

    return { progress, currentLabel, isComplete, hasError };
  }, [steps]);

  // Valeur du contexte
  const contextValue: LoadingContextValue = useMemo(
    () => ({
      // État
      steps,
      progress: computedValues.progress,
      currentLabel: computedValues.currentLabel,
      isComplete: computedValues.isComplete,
      hasError: computedValues.hasError,
      errorMessage,

      // Actions
      initSteps,
      startStep,
      completeStep,
      failStep,
      reset,
    }),
    [
      steps,
      computedValues,
      errorMessage,
      initSteps,
      startStep,
      completeStep,
      failStep,
      reset,
    ],
  );

  return (
    <LoadingProgressContext.Provider value={contextValue}>
      {children}
    </LoadingProgressContext.Provider>
  );
}
